import React, { useState, useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, useMap, useMapEvents, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import omnivore from "leaflet-omnivore";
import styles from "./MapComponent.module.css";

const MapComponent = ({ guardsData, selectedGuardId, setSelectedGuardId, selectedGuardB, carStatus }) => {
  const [map, setMap] = useState(null);
  const [buttonColor, setButtonColor] = useState("blue");
  const [shouldFocus, setShouldFocus] = useState(false);
  const [shouldFocusRace, setShouldFocusRace] = useState(false);
  const [singleFocus, setSingleFocus] = useState("no");
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [crossCoords, setCrossCoords] = useState({ lat: 37.97, lon: 23.73 });
  const [zoomLevel, setZoomLevel] = useState(16);
  const [permanentPoints, setPermanentPoints] = useState([]);
  const markersRef = useRef({});
  const previousPositions = useRef({});
  const [mapSource, setMapSource] = useState("mapbox");
  const [routeLines, setRouteLines] = useState([]);
  const [routeLine, setRouteLine] = useState(null);
  const [blinkTrigger, setBlinkTrigger] = useState(false);

  // Helper functions for polyline animation
  const closestPointOnSegment = (point, segmentStart, segmentEnd) => {
    const x = point.lat;
    const y = point.lng;
    const x1 = segmentStart.lat;
    const y1 = segmentStart.lng;
    const x2 = segmentEnd.lat;
    const y2 = segmentEnd.lng;

    const A = x - x1;
    const B = y - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    let param = -1;

    if (len_sq !== 0) param = dot / len_sq;

    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    return L.latLng(xx, yy);
  };

  const isPointNearPolyline = (point, polylineCoords, maxDistance = 50) => {
    if (!polylineCoords || polylineCoords.length < 2) return false;

    const pointLatLng = L.latLng(point);
    let minDistance = Infinity;

    for (let i = 0; i < polylineCoords.length - 1; i++) {
      const segmentStart = L.latLng(polylineCoords[i]);
      const segmentEnd = L.latLng(polylineCoords[i + 1]);
      const closestPoint = closestPointOnSegment(pointLatLng, segmentStart, segmentEnd);
      const distance = pointLatLng.distanceTo(closestPoint);

      if (distance < minDistance) {
        minDistance = distance;
      }

      if (minDistance <= maxDistance) break;
    }

    return minDistance <= maxDistance;
  };

  const findClosestPointOnPolyline = (point, polylineCoords) => {
    let closestPoint = null;
    let minDistance = Infinity;

    for (let i = 0; i < polylineCoords.length - 1; i++) {
      const segmentStart = L.latLng(polylineCoords[i]);
      const segmentEnd = L.latLng(polylineCoords[i + 1]);
      const currentClosest = closestPointOnSegment(point, segmentStart, segmentEnd);
      const distance = point.distanceTo(currentClosest);

      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = currentClosest;
      }
    }

    return closestPoint;
  };

  const findSegmentIndex = (point, polylineCoords) => {
    for (let i = 0; i < polylineCoords.length - 1; i++) {
      const segmentStart = L.latLng(polylineCoords[i]);
      const segmentEnd = L.latLng(polylineCoords[i + 1]);
      const closest = closestPointOnSegment(point, segmentStart, segmentEnd);
      if (closest.distanceTo(point) < 1) {
        return i;
      }
    }
    return -1;
  };

  const animateMarker = (marker, startLatLng, targetLatLng, duration = 9800) => {
    if (!marker) return;

    const shouldAnimateOnPolyline = () => {
      const guard = guardsData.find((g) => L.latLng(g.lat, g.lon).equals(targetLatLng) || L.latLng(g.lat, g.lon).equals(startLatLng));

      if (!guard || guard.speed <= 10) return false;

      for (const line of routeLines) {
        if (isPointNearPolyline(startLatLng, line.coordinates) && isPointNearPolyline(targetLatLng, line.coordinates)) {
          return line.coordinates;
        }
      }
      return false;
    };

    const polylineCoords = shouldAnimateOnPolyline();

    if (polylineCoords) {
      animateMarkerAlongPolyline(marker, startLatLng, targetLatLng, polylineCoords, duration);
    } else {
      animateLinear(marker, startLatLng, targetLatLng, duration);
    }
  };

  const animateLinear = (marker, startLatLng, targetLatLng, duration) => {
    const latDiff = targetLatLng.lat - startLatLng.lat;
    const lngDiff = targetLatLng.lng - startLatLng.lng;

    let startTime;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const currentLat = startLatLng.lat + latDiff * progress;
      const currentLng = startLatLng.lng + lngDiff * progress;

      marker.setLatLng([currentLat, currentLng]);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  };

  const animateMarkerAlongPolyline = (marker, startLatLng, targetLatLng, polylineCoords, duration) => {
    const startOnLine = findClosestPointOnPolyline(startLatLng, polylineCoords);
    const targetOnLine = findClosestPointOnPolyline(targetLatLng, polylineCoords);

    //  const startOnLine = startLatLng;
    //  const targetOnLine = targetLatLng;

    const startSegment = findSegmentIndex(startOnLine, polylineCoords);
    const targetSegment = findSegmentIndex(targetOnLine, polylineCoords);

    if (startSegment === -1 || targetSegment === -1) {
      animateLinear(marker, startLatLng, targetLatLng, duration);
      return;
    }

    const forwardDistance = calculatePolylineDistance(startOnLine, targetOnLine, polylineCoords, startSegment, targetSegment);
    const reverseDistance = calculatePolylineDistance(targetOnLine, startOnLine, polylineCoords, targetSegment, startSegment);

    const isForward = forwardDistance <= reverseDistance;
    const totalDistance = isForward ? forwardDistance : reverseDistance;

    let startTime;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const targetDistance = progress * totalDistance;

      const currentPos = isForward ? getPointAtDistance(startOnLine, targetDistance, polylineCoords, startSegment, targetSegment) : getPointAtDistance(targetOnLine, totalDistance - targetDistance, polylineCoords, targetSegment, startSegment);

      marker.setLatLng([currentPos.lat, currentPos.lng]);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  };

  const calculatePolylineDistance = (startPoint, endPoint, polylineCoords, startSegment, endSegment) => {
    if (startSegment <= endSegment) {
      let distance = 0;
      const startSegmentEnd = L.latLng(polylineCoords[startSegment + 1]);
      distance += startPoint.distanceTo(startSegmentEnd);

      for (let i = startSegment + 1; i < endSegment; i++) {
        distance += L.latLng(polylineCoords[i]).distanceTo(L.latLng(polylineCoords[i + 1]));
      }

      const endSegmentStart = L.latLng(polylineCoords[endSegment]);
      distance += endSegmentStart.distanceTo(endPoint);

      return distance;
    } else {
      let distance = 0;
      const startSegmentStart = L.latLng(polylineCoords[startSegment]);
      distance += startPoint.distanceTo(startSegmentStart);

      for (let i = startSegment; i > endSegment + 1; i--) {
        distance += L.latLng(polylineCoords[i]).distanceTo(L.latLng(polylineCoords[i - 1]));
      }

      const endSegmentEnd = L.latLng(polylineCoords[endSegment + 1]);
      distance += endSegmentEnd.distanceTo(endPoint);

      return distance;
    }
  };

  const getPointAtDistance = (startPoint, distance, polylineCoords, startSegment, targetSegment) => {
    let remainingDistance = distance;

    if (startSegment <= targetSegment) {
      const startSegmentEnd = L.latLng(polylineCoords[startSegment + 1]);
      const segmentDistance = startPoint.distanceTo(startSegmentEnd);

      if (remainingDistance <= segmentDistance) {
        const ratio = remainingDistance / segmentDistance;
        return L.latLng(startPoint.lat + (startSegmentEnd.lat - startPoint.lat) * ratio, startPoint.lng + (startSegmentEnd.lng - startPoint.lng) * ratio);
      }

      remainingDistance -= segmentDistance;

      let currentSegment = startSegment + 1;
      while (currentSegment < targetSegment && remainingDistance > 0) {
        const segmentStart = L.latLng(polylineCoords[currentSegment]);
        const segmentEnd = L.latLng(polylineCoords[currentSegment + 1]);
        const segmentDistance = segmentStart.distanceTo(segmentEnd);

        if (remainingDistance <= segmentDistance) {
          const ratio = remainingDistance / segmentDistance;
          return L.latLng(segmentStart.lat + (segmentEnd.lat - segmentStart.lat) * ratio, segmentStart.lng + (segmentEnd.lng - segmentStart.lng) * ratio);
        }

        remainingDistance -= segmentDistance;
        currentSegment++;
      }

      const targetPoint = findClosestPointOnPolyline(L.latLng(polylineCoords[targetSegment + 1]), polylineCoords);
      const segmentStart = L.latLng(polylineCoords[targetSegment]);

      const ratio = Math.min(remainingDistance / segmentStart.distanceTo(targetPoint), 1);
      return L.latLng(segmentStart.lat + (targetPoint.lat - segmentStart.lat) * ratio, segmentStart.lng + (targetPoint.lng - segmentStart.lng) * ratio);
    } else {
      const startSegmentStart = L.latLng(polylineCoords[startSegment]);
      const segmentDistance = startPoint.distanceTo(startSegmentStart);

      if (remainingDistance <= segmentDistance) {
        const ratio = remainingDistance / segmentDistance;
        return L.latLng(startPoint.lat + (startSegmentStart.lat - startPoint.lat) * ratio, startPoint.lng + (startSegmentStart.lng - startPoint.lng) * ratio);
      }

      remainingDistance -= segmentDistance;

      let currentSegment = startSegment;
      while (currentSegment > targetSegment + 1 && remainingDistance > 0) {
        const segmentEnd = L.latLng(polylineCoords[currentSegment]);
        const segmentStart = L.latLng(polylineCoords[currentSegment - 1]);
        const segmentDistance = segmentStart.distanceTo(segmentEnd);

        if (remainingDistance <= segmentDistance) {
          const ratio = remainingDistance / segmentDistance;
          return L.latLng(segmentEnd.lat + (segmentStart.lat - segmentEnd.lat) * ratio, segmentEnd.lng + (segmentStart.lng - segmentEnd.lng) * ratio);
        }

        remainingDistance -= segmentDistance;
        currentSegment--;
      }

      const targetPoint = findClosestPointOnPolyline(L.latLng(polylineCoords[targetSegment]), polylineCoords);
      const segmentEnd = L.latLng(polylineCoords[targetSegment + 1]);

      const ratio = Math.min(remainingDistance / segmentEnd.distanceTo(targetPoint), 1);
      return L.latLng(segmentEnd.lat + (targetPoint.lat - segmentEnd.lat) * ratio, segmentEnd.lng + (targetPoint.lng - segmentEnd.lng) * ratio);
    }
  };

  // Rest of the component code remains the same...
  useEffect(() => {
    if (map) {
      console.log("Map instance is ready:", map);
    }
  }, [map]);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/permanentPoints.json`)
      .then((response) => response.json())
      .then((data) => {
        const points = data.filter((p) => !p.type || p.type !== "line");
        const lines = data.filter((p) => p.type === "line");
        setPermanentPoints(points);
        setRouteLines(lines);
      })
      .catch((error) => console.error("Error loading permanent points:", error));
  }, []);

  const handleToggleMapSource = () => {
    const mapOptions = ["mapbox", "googleSimple", "googleSatellite"];
    const currentIndex = mapOptions.indexOf(mapSource);
    setMapSource(mapOptions[(currentIndex + 1) % mapOptions.length]);
  };

  const focusAllGuards = () => {
    setShouldFocus(true);
    setSingleFocus(false);
  };

  const focusSingleGuard = () => {
    setShouldFocus(true);
    setSingleFocus("once");
  };

  const focusRace = () => {
    setShouldFocusRace(true);
  };

  const trackSingleGuard = () => {
    setShouldFocus(true);
    if (singleFocus === "once" || singleFocus === "no") {
      setSingleFocus("continuous");
      setShouldFocus(true);
    } else {
      setSingleFocus("no");
    }
  };

  const ZoomWatcher = () => {
    useMapEvents({
      zoomend: (e) => {
        setZoomLevel(e.target.getZoom());
      },
    });
    return null;
  };

  const calculateBounds = (coordsArray) => new L.LatLngBounds(coordsArray);

  const FitBounds = ({ guards, shouldFocus, setShouldFocus, singleFocus }) => {
    const map = useMapEvents({
      move() {
        const center = map.getCenter();
        setCrossCoords({ lat: center.lat, lon: center.lng });
      },
    });

    const permanentPointCoords = permanentPoints.map((p) => p.coordinates);
    const lineCoords = routeLine?.coordinates || [];
    const allCoords = [...permanentPointCoords, ...lineCoords];

    const bounds = useMemo(() => {
      return allCoords.length > 0 ? calculateBounds(allCoords) : null;
    }, [permanentPoints, routeLine]);

    useEffect(() => {
      if (bounds && shouldFocus) {
        if (singleFocus === "once" || singleFocus === "continuous") {
          if (selectedGuardB) {
            // Smooth fly to single guard
            map.flyTo([selectedGuardB.lat, selectedGuardB.lon], zoomLevel, {
              duration: 1.5, // Animation duration in seconds
              easeLinearity: 0.25,
            });
          }
        } else {
          // Smooth fly to all bounds for "Focus All Race"
          map.flyToBounds(bounds, {
            padding: [50, 50], // Add some padding
            duration: 2, // Animation duration in seconds
            easeLinearity: 0.25,
          });
        }
        setShouldFocus(false);
      }
    }, [bounds, shouldFocus, singleFocus, map, guards.length, selectedGuardB]);

    return null;
  };

  useEffect(() => {
    if (!selectedGuardB || selectedGuardB.lon === null || selectedGuardB.lat === null) {
      return;
    }
    setLat(selectedGuardB.lat);
    setLon(selectedGuardB.lon);

    if (singleFocus === "continuous" && selectedGuardB) {
      setShouldFocus(true);
    }
  }, [selectedGuardB, singleFocus]);

  const getIcon = (color, carNo, isSquare = false, fontColor = "black", size = 24) => {
    const shapeStyle = isSquare ? "border-radius: 0;" : "border-radius: 50%;";

    const displayCarNo = carNo !== undefined && carNo !== null ? carNo : "";

    return L.divIcon({
      className: "custom-icon",
      html: `
        <div style="
          width:  ${size}px;
          height:  ${size}px;
          background-color: ${color}; 
          ${shapeStyle} 
          display: flex; 
          justify-content: center; 
          align-items: center;
          color:  ${fontColor}; 
          font-weight: 900;
          font-size: 12px;
          border: 1px solid #333;">
          ${displayCarNo}
        </div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  };

  // Add this effect to make the blinkTrigger update regularly
useEffect(() => {
  const interval = setInterval(() => {
    setBlinkTrigger(prev => !prev);
  }, 500); // Blink every 500ms
  return () => clearInterval(interval);
}, []);

// Modified getStatusColor
const getStatusColor = (guard) => {
  const baseColor = guard.color || "grey";
  
  const shouldBlink = guard.speed == 0 && 
    routeLines.some(line => isPointNearPolyline(
      L.latLng(guard.lat, guard.lon), 
      line.coordinates
    ));

  return shouldBlink ? 
    (blinkTrigger ? baseColor : "red") : 
    baseColor;
};


  const getTooltipClass = (guard) => {
    switch (guard.color) {
      case "red":
        return styles.tootipRed;
      case "orange":
        return styles.tootipOrange;
      case "yellow":
        return styles.tootipYellow;
      case "green":
        return styles.tootipGreen;
      case "blue":
        return styles.tootipBlue;
      case "brown":
        return styles.tootipBrown;
      case "lightblue":
        return styles.tooltiplightBlue;
      default:
        return styles.tootipGrey;
    }
  };

  const getTooltipMsg = (guard) => {
    return guard.IncMSG ? styles.msgPresent : "";
  };

  const getButtonStyle = () => {
    return {
      position: "absolute",
      right: "10px",
      bottom: singleFocus === "continuous" ? "100px" : "101px",
      zIndex: "1000",
      borderRadius: "18px",
      backgroundColor: singleFocus === "continuous" ? "orange" : "grey",
    };
  };

  const getButtonText = () => {
    return singleFocus === "continuous" ? "Car Tracking On" : "Car Tracking Off";
  };

  const formatCoords = (val, isLat) => {
    const degrees = Math.floor(val);
    const minutes = Math.floor((val - degrees) * 60);
    const seconds = ((val - degrees) * 60 - minutes) * 60;

    const paddedDegrees = String(Math.abs(degrees)).padStart(2, "0");
    const paddedMinutes = String(Math.abs(minutes)).padStart(2, "0");
    const paddedSeconds = String(Math.abs(seconds.toFixed(1))).padStart(2, "0");

    const direction = val >= 0 ? (isLat ? "N" : "E") : isLat ? "S" : "W";
    return `${paddedDegrees}°${paddedMinutes}'${paddedSeconds}"${direction}`;
  };

  const KmlLayer = ({ url }) => {
    const map = useMap();
    const [kmlLayer, setKmlLayer] = useState(null);

    useEffect(() => {
      if (!map || !url) return;

      if (kmlLayer) {
        map.removeLayer(kmlLayer);
      }

      const newKmlLayer = omnivore
        .kml(url)
        .on("ready", function () {
          const bounds = newKmlLayer.getBounds();
          if (bounds.isValid() && shouldFocusRace) {
            map.fitBounds(bounds);
            setShouldFocusRace(false);
          }
        })
        .addTo(map);

      setKmlLayer(newKmlLayer);

      return () => {
        if (newKmlLayer) {
          map.removeLayer(newKmlLayer);
        }
      };
    }, [map, url, shouldFocusRace]);

    return null;
  };

  useEffect(() => {
    guardsData.forEach((guard) => {
      const markerRef = markersRef.current[guard.serial];
      if (markerRef) {
        const targetLatLng = L.latLng(guard.lat, guard.lon);
        const previousLatLng = previousPositions.current[guard.serial];

        if (!previousLatLng) {
          previousPositions.current[guard.serial] = targetLatLng;
          return;
        }

        if (!L.latLng(previousLatLng).equals(targetLatLng)) {
          animateMarker(markerRef, previousLatLng, targetLatLng, 9800);
          previousPositions.current[guard.serial] = targetLatLng;
        }
      }
    });
  }, [guardsData]);



  return (
    <div className="mapcontainer" style={{ width: "100%", height: "100%", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          left: "6vw",
          top: "23vh",
          zIndex: "1000",
          borderRadius: "1px",
          color: "red",
          fontWeight: "bold",
          alignItems: "center",
        }}>
        ZOOM LEVEL
      </div>
      <MapContainer
        center={[39.61, 20.79]}
        zoom={6}
        style={{ width: "100%", height: "100%" }}
        whenCreated={(mapInstance) => {
          setMap(mapInstance);
        }}
        zoomControl={true}
        scrollWheelZoom={true}>
        <ZoomWatcher />
        {mapSource === "mapbox" && (
          <TileLayer
            url={`https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiYXJrYWRvbmlrb3MiLCJhIjoiY20xczhyMGM2MDQ1ejJqc2p1YWM0cjBsbCJ9.J9xhMre3w3wK66hD8yqx9A`}
            attribution="&copy; <a href='https://www.mapbox.com/about/maps/'>Mapbox</a>"
          />
        )}
        {mapSource === "googleSimple" && <TileLayer url="http://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" attribution="&copy; <a href='https://www.google.com/maps'>Google Maps</a>" />}
        {mapSource === "googleSatellite" && <TileLayer url="http://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" attribution="&copy; <a href='https://www.google.com/maps'>Google Maps</a>" />}

        {guardsData
          .filter((guard) => guard.serial !== selectedGuardId)
          .map((guard) => {
            const status = carStatus[guard.serial];
            if (status === "Car off") return null;

            return (
              <Marker key={guard.serial} position={[guard.lat, guard.lon]} icon={getIcon(getStatusColor(guard), guard.carNo, guard.title === "Official")} ref={(el) => (markersRef.current[guard.serial] = el)}>
                {status === "Speed on" && (
                  <Tooltip permanent direction="left" offset={[-20, 0]} className={`leaflet-tooltip-custom ${getTooltipClass(guard)} ${getTooltipMsg(guard)}`}>
                    <div className={styles.tooltipContent}>{guard.speed} Km/h</div>
                  </Tooltip>
                )}
              </Marker>
            );
          })}

        {guardsData
          .filter((guard) => guard.serial === selectedGuardId)
          .map((guard) => {
            const status = carStatus[guard.serial];
            if (status === "Car off") return null;

            return (
              <React.Fragment key={guard.serial}>
                <Marker position={[guard.lat, guard.lon]} icon={getIcon(getStatusColor(guard), guard.carNo, guard.title === "Official")} ref={(el) => (markersRef.current[guard.serial] = el)} zIndexOffset={12000}>
                  {status === "Speed on" && (
                    <Tooltip permanent direction="left" offset={[20, 0]} className={`leaflet-tooltip-custom ${getTooltipClass(guard)} ${getTooltipMsg(guard)}`}>
                      <div className={styles.tooltipContent}>{guard.speed} Km/h</div>
                    </Tooltip>
                  )}
                </Marker>
              </React.Fragment>
            );
          })}

        {zoomLevel >= 14 &&
          permanentPoints.map((point, index) => (
            <Marker key={index} position={point.coordinates} icon={getIcon("red", "", false, "white", 8)}>
              <Tooltip permanent direction="right" offset={[30, 0]} className="custom-red-tooltip">
                {point.name}
              </Tooltip>
            </Marker>
          ))}

{zoomLevel >= 11 &&
  (() => {
    const renderedNames = new Set();

    return routeLines.map((line, idx) => {
      const showTooltip = !renderedNames.has(line.name);
      renderedNames.add(line.name);

      return (
        <Polyline
          key={`line-${idx}`}
          positions={line.coordinates}
          pathOptions={{
            color: line.color || "blue",
            weight: parseInt(line.weight || 4),
            opacity: 0.9,
          }}
        >
          {showTooltip && (
            <Tooltip permanent direction="top" offset={[0, -10]}>
              {line.name}
            </Tooltip>
          )}
        </Polyline>
      );
    });
  })()}


        <FitBounds guards={guardsData} shouldFocus={shouldFocus} setShouldFocus={setShouldFocus} singleFocus={singleFocus} selectedGuardId={selectedGuardId} />
      </MapContainer>

      {selectedGuardB &&
        singleFocus === "continuous" &&
        (() => {
          return (
            <div
              style={{
                position: "absolute",
                left: "calc(3vw + 130px)",
                bottom: "calc(9vh + 0px)",
                zIndex: 13000,
                fontSize: "1.5em",
                fontWeight: "bold",
                color: "red",
                whiteSpace: "nowrap",
              }}>
              {selectedGuardB.speed} Km/h
            </div>
          );
        })()}

      <div className={styles.crosshair}></div>
      <div className={styles.coordinates}>
        {formatCoords(crossCoords.lat, true)} <br />
        {formatCoords(crossCoords.lon, false)}
        <br />
        <br />
        {crossCoords.lat.toFixed(6)}
        <br />
        {crossCoords.lon.toFixed(6)}
      </div>

      <div
        style={{
          position: "absolute",
          right: "10px",
          bottom: "20px",
          zIndex: "1000",
        }}>
        <label htmlFor="zoomLevel" style={{ fontWeight: "bold", textShadow: "2px 2px 2px white" }}>
          Focus Zoom Level: {zoomLevel}
        </label>
        <input type="range" id="zoomLevel" min="12" max="20" step="0.1" value={zoomLevel} onChange={(e) => setZoomLevel(parseInt(e.target.value, 10))} style={{ width: "100%" }} />
      </div>

      <button style={getButtonStyle()} onClick={trackSingleGuard}>
        {getButtonText()}
      </button>
      <button
        className={styles["button-24"]}
        style={{
          position: "absolute",
          right: "10px",
          bottom: "60px",
          zIndex: "1000",
          borderRadius: "18px",
        }}
        onClick={focusSingleGuard}>
        FOCUS CAR
      </button>
      <button
        className={styles["button-24"]}
        style={{
          position: "absolute",
          right: "10px",
          bottom: "140px",
          zIndex: "1000",
          borderRadius: "8px",
        }}
        onClick={focusAllGuards}>
        FOCUS ALL RACE
      </button>

      <button
        style={{
          position: "absolute",
          top: "13px",
          left: "33vw",
          zIndex: "1000",
          padding: "10px 20px",
          backgroundColor: "#007BFF",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
        }}
        onClick={handleToggleMapSource}>
        Switch to {mapSource === "mapbox" ? "Google StreetMap" : mapSource === "googleSimple" ? "Google Satellite" : "Mapbox"}
      </button>

      <div
        style={{
          position: "absolute",
          right: "10px",
          top: "10px",
          zIndex: "1000",
          borderRadius: "1px",
        }}>
        <img style={{ width: "calc(5vmin + 5vmax)", height: "auto" }} src={`${process.env.PUBLIC_URL}/assets/eleta.jpg`} alt="ELETA Logo" className="title-logo" />
      </div>
      <div
        style={{
          position: "absolute",
          left: "10px",
          top: "10px",
          zIndex: "1000",
          borderRadius: "1px",
        }}>
        <img style={{ width: "calc(5vmin + 5vmax)", height: "auto" }} src={`${process.env.PUBLIC_URL}/assets/omae.png`} alt="OMAE Logo" className="title-logo" />
      </div>
    </div>
  );
};

export default MapComponent;
